/**
 * Script runner — dispatches a scriptRef to a Handler.
 *
 * Eng-review decision 13 shapes ScriptCtx. Decision 21 shapes the
 * three-layer dispatch: synthetic prefixes (allowlist) → heal-spot set →
 * explicit registry. Layer 4 is a dev-mode `console.warn` for refs that
 * don't match any of the three — production silently ignores them so a
 * map with a missing handler doesn't crash the run.
 *
 * Handlers are pure functions of `ScriptCtx`. They never touch Phaser
 * primitives directly — instead they call methods on `ctx.worldScene`
 * (typed as `ScriptHostScene`, a narrow view of WorldScene) so the
 * entangled bits (transitioning flag, fade, camera bounds, dialogue
 * overlay) stay encapsulated. Future PRs (Oak intercept, rival
 * cinematic) compose `await ctx.worldScene.runHandlerBracket(async () =>
 * { ... })` which owns transitioning + camera-lock for the duration.
 *
 * Per decision 20: setFlag does NOT autosave. Handlers explicitly call
 * `ctx.worldScene.save()` at handler-end.
 */
import type Phaser from 'phaser';
import { runState } from '../state/runState';
import { audio } from '../systems/audio';
import { FLAGS } from './flagRegistry';
import type { Tile } from './npcWalkPath';

/** Narrow view of WorldScene that handlers are allowed to touch. Keeps
 *  the entangled-with-Phaser concerns (transitioning, camera bounds,
 *  fade/dialogue lifecycle, map transitions) inside WorldScene where
 *  they belong, while handlers stay testable as pure functions. */
export interface ScriptHostScene {
  fade(direction: 'in' | 'out'): Promise<void>;
  showDialogue(pages: string[], scriptRef: string | null): void;
  setTransitioning(value: boolean): void;
  /** Look up trainer data by ref + launch BattleScene with their party. */
  launchTrainerBattleByRef(realRef: string): void;
  /** Walk the player back to the Pallet south-edge connection — used by
   *  the placeholder `__pallet_bounce__` synthetic ref (out-of-scope
   *  northbound exits from Pallet pre-Oak-intro). */
  bouncePalletConnection(): void;
  /** Bracket primitive for cinematic handlers (Oak intercept, rival
   *  cinematic). Owns transitioning=true + camera bounds for the
   *  duration of the wrapped async function. Throws if the function
   *  throws (state still restored). opts.recallFollower (round-5,
   *  HGSS convention): recall the follower into its ball for the
   *  bracket + send it back out at bracket-end — opt in ONLY when the
   *  cinematic's walk geometry could cross the follower's tile (the
   *  rival post-battle exit). Idempotent when no follower is out;
   *  depth-guarded across nested opt-in brackets.
   *  opts.restoreFollower (round-6, default true): pass false for
   *  brackets that END by launching a battle (rival pre-battle
   *  challenge) — the recall already matches the battle SLEEP's
   *  follower state and the post-battle WAKE re-derive is the
   *  restore; a bracket-end pop would race the battle fade. */
  runHandlerBracket<T>(
    fn: () => Promise<T>,
    opts?: { recallFollower?: boolean; restoreFollower?: boolean },
  ): Promise<T>;
  /** Persist runState to localStorage. Called explicitly at handler-end
   *  per decision 20 (setFlag does NOT autosave). */
  save(): void;
  /** C5 scripted-walking primitive (PR 4). Creates a handler-owned
   *  sprite at `startTile`, walks it cardinally through `waypoints` at
   *  `pixelsPerSecond` (default 32), and resolves when the walk ends.
   *  Per decision 15: the sprite is NOT pushed onto `npcSprites[]` so
   *  rebuildNpcSprites can't yank it mid-walk; the caller owns lifetime.
   *  Holds transitioning=true for the duration. Suspends per-tween when
   *  dialogue opens, resumes on close. Player-blocked target tile: NPC
   *  waits, no phase-through. */
  npcWalkAlongPath(args: NpcWalkArgs): Promise<NpcWalkHandle>;
  /** PR 13b: poll until any open dialogue overlay has closed. Used by
   *  cinematic handlers that showDialogue and then need to resume
   *  after the player taps through. Polls at 50 ms. */
  waitForDialogueClose(): Promise<void>;
  /** PR 13b: current player tile coords. Read-only snapshot — handlers
   *  should not mutate via this getter; use transitionToMap or the
   *  bracket primitive for repositioning. */
  getPlayerTile(): { x: number; y: number };
  /** PR 12.5: rebuild NPC sprites against the current runState. Call
   *  after mutating FLAG_HIDE_* flags so the world reflects the new
   *  visibility. (No-op when mapData is null.) */
  refreshNpcVisibility(): void;
  /** PR 13b: transition the player to a different map with a fade. The
   *  cinematic-ending warp for the Oak intercept (Pallet exterior →
   *  Oak's Lab door rug). `name` is a runtime map basename matching
   *  V01_MAPS; `entryFacing` controls the autoStepOut direction (e.g.
   *  'down' to land south of the warp tile). */
  transitionToMap(
    name: string,
    entryX: number,
    entryY: number,
    entryFacing?: 'up' | 'down' | 'left' | 'right',
  ): Promise<void>;
  /** Launch DraftScene with the (already-rolled) starter offers. Reads
   *  offers from runState.getPendingDraft(); no-op if no pending draft
   *  is cached. Lab-flow redesign (2026-08-02): fired by oakIntercept
   *  at speech-end and by the state-aware Oak tap as the retry path —
   *  ball taps no longer launch the draft. */
  launchStarterDraft(): Promise<void>;
  /** PR 14.5: cinematic-drive the player sprite along a cardinal path.
   *  Bypasses the input gate (works while runHandlerBracket holds
   *  transitioning=true). Tweens player.x/y, updates playerTileX/Y +
   *  follower hooks + sprite frame per step. Sister to npcWalkAlongPath
   *  but operates on the player sprite. */
  walkPlayerAlongPath(args: {
    waypoints: readonly Tile[];
    pixelsPerSecond?: number;
    /** Map the waypoints were authored for — the walk no-ops with a
     *  warn if the player is elsewhere (round-4 spin guard). */
    expectedMap?: string;
  }): Promise<void>;
  /** Turn the player in place to face the given direction. Used by
   *  cinematic handlers when the player should pivot toward an NPC for
   *  dialogue without moving onto an adjacent tile (e.g. Oak intercept
   *  beat 3 — player turns south to face Oak before "It's unsafe!"). */
  setPlayerFacing(dir: 'up' | 'down' | 'left' | 'right'): void;
  /** PR A1: set a specific NPC sprite's frame by `localId`. Looks up
   *  the sprite in the WorldScene's `npcSprites` Map; no-op if the
   *  NPC isn't currently mounted (hidden via flag, or wrong map). Used
   *  by `oakIntercept` to play the rival's react-bob animation (cartridge
   *  `Common_Movement_WalkInPlaceFasterUp` × 2) — the rival's static
   *  NPC sprite frame alternates between standing-up + walkA-up frames
   *  in a 250ms cadence × 2 cycles without spawning a handler-owned
   *  walker for that brief beat. */
  setNpcFrame(localId: string, frameIndex: number): void;
  /** PR A2-prep: pause the cinematic for `ms` milliseconds before
   *  awaiting the next beat. Cartridge cinematics use `delay N` between
   *  message boxes (e.g., 60 frames after warp-in before the first
   *  rival interjection); this is the same primitive on top of Phaser's
   *  scheduler. Resolves on Phaser's `time.delayedCall` so reduce-motion
   *  + tab-suspend pauses behave consistently with the rest of the
   *  cinematic surface. */
  delay(ms: number): Promise<void>;
}

export interface NpcWalkArgs {
  /** Phaser texture key — must be loaded via SPRITE_MANIFEST. Ignored
   *  when `existingSprite` is provided (the existing sprite's texture
   *  is preserved). */
  spriteKey: string;
  /** Tile-space start position. Sprite is mounted here as frame 0
   *  when creating fresh; ignored when `existingSprite` is provided
   *  (the existing sprite's current position is the start). */
  startTile: Tile;
  /** Ordered cardinal waypoints. Each waypoint is reached by resolving
   *  X then Y from the prior position (no diagonals). */
  waypoints: readonly Tile[];
  /** Walk speed in pixels per second. Default 32 (one 16-px tile every
   *  500 ms — cartridge feel). */
  pixelsPerSecond?: number;
  /** If true, sprite.destroy() runs when the walk completes. Default
   *  false — the caller owns lifetime (e.g. an Oak cinematic that
   *  reuses the sprite for a follow-up beat). */
  destroyOnComplete?: boolean;
  /** When provided, walk THIS sprite instead of creating a new one.
   *  Used for multi-leg cinematics (Oak walks down to player, then
   *  Oak + player walk together to the lab). The existing sprite's
   *  current tile position must match `startTile`. */
  existingSprite?: Phaser.GameObjects.Sprite;
}

export interface NpcWalkHandle {
  /** The handler-owned Phaser sprite. Caller must destroy() it unless
   *  the walk was launched with destroyOnComplete=true. */
  sprite: Phaser.GameObjects.Sprite;
}

export interface ScriptCtx {
  worldScene: ScriptHostScene;
  runState: typeof runState;
  audio: typeof audio;
  FLAGS: typeof FLAGS;
}

export type Handler = (ctx: ScriptCtx, ref: string) => void | Promise<void>;

// ─── Layer 1: synthetic-prefix allowlist (decision 21) ──────────────
//
// Synthetic refs are NOT in any runtime.json — WorldScene mints them at
// runtime (e.g. `__trainer__:<realRef>` from trainer-LOS detection). They
// satisfy the dialogue-coverage lint without orphan-key noise.

interface PrefixHandler {
  prefix: string;
  handler: Handler;
}

const SYNTHETIC_PREFIX_HANDLERS: ReadonlyArray<PrefixHandler> = [
  {
    prefix: '__trainer__:',
    handler: (ctx, ref) => {
      const realRef = ref.slice('__trainer__:'.length);
      ctx.worldScene.launchTrainerBattleByRef(realRef);
    },
  },
  {
    prefix: '__pallet_bounce__',
    handler: (ctx) => {
      ctx.worldScene.bouncePalletConnection();
    },
  },
];

// PR A3: synthetic refs dispatched from the WorldScene WAKE handler
// when the lab cinematic state machine + flag state indicate a
// pending cinematic. NOT in any runtime.json — minted at runtime by
// the WAKE-handler gate. The dialogue-coverage lint (PR A6) reads this
// allowlist so the refs don't show up as orphans.
const SYNTHETIC_EXACT_HANDLERS: Record<string, Handler> = {};
export function registerSyntheticHandler(ref: string, handler: Handler): void {
  SYNTHETIC_EXACT_HANDLERS[ref] = handler;
}
/** Read-only view for the dialogue-coverage lint (PR A6). */
export function listSyntheticHandlers(): readonly string[] {
  return Object.keys(SYNTHETIC_EXACT_HANDLERS);
}

// ─── Layer 2: heal-spot membership ──────────────────────────────────
//
// Nurse scriptRefs route through a common heal handler. Adding a Center
// = 1 row in this Set + a dialogue entry. The handler is a pure function
// of ScriptCtx; the existing M3 fade-out → healAll → save → fade-in →
// status-cleared ack lives here, not in WorldScene.

export const HEAL_SPOT_SCRIPT_REFS = new Set<string>([
  'PewterCity_PokemonCenter_1F_EventScript_Nurse',
  'ViridianCity_PokemonCenter_1F_EventScript_Nurse',
]);

const healHandler: Handler = async (ctx) => {
  // One-tap Centers skip the greeting page, so an empty-party tap must
  // still say SOMETHING — the greeting is the empty-party fallback.
  if (ctx.runState.getParty().length === 0) {
    ctx.worldScene.showDialogue(
      ['Welcome to the POKEMON CENTER!', 'Come back with a POKEMON any time.'],
      null,
    );
    return;
  }
  ctx.worldScene.setTransitioning(true);
  await ctx.worldScene.fade('out');
  ctx.runState.healAll();
  ctx.worldScene.save();
  ctx.audio.playSfx('heal');
  await ctx.worldScene.fade('in');
  ctx.worldScene.setTransitioning(false);
  // Cartridge acknowledgment — Pokémon-Center status-clear dual-line.
  // scriptRef = null so onDialogueClosed doesn't try to recurse a
  // sub-action on tap.
  ctx.worldScene.showDialogue(
    ['Your Pokémon are fully healed and status conditions cleared!'],
    null,
  );
};

// ─── Layer 3: explicit registry ─────────────────────────────────────
//
// Handlers for specific cartridge scriptRefs (Oak intercept, rival
// cinematic, item-ball pickups, etc.) register here. Empty in PR 3;
// future PRs add entries via `registerScript(ref, handler)`. The
// dialogue-coverage lint (PR 18) walks this registry + the synthetic
// prefix list + HEAL_SPOT_SCRIPT_REFS to verify every runtime.json
// scriptRef has a handler OR a DIALOGUE entry.

const EXPLICIT_HANDLERS: Record<string, Handler> = {};

export function registerScript(scriptRef: string, handler: Handler): void {
  EXPLICIT_HANDLERS[scriptRef] = handler;
}

/** Read-only view for the dialogue-coverage lint (PR 18). */
export function listRegisteredScripts(): readonly string[] {
  return Object.keys(EXPLICIT_HANDLERS);
}

// ─── Dispatch ───────────────────────────────────────────────────────

export function dispatchScript(ref: string, ctx: ScriptCtx): void {
  // Layer 1: synthetic prefixes
  for (const { prefix, handler } of SYNTHETIC_PREFIX_HANDLERS) {
    if (ref.startsWith(prefix)) {
      void handler(ctx, ref);
      return;
    }
  }
  // Layer 1.5 (PR A3): synthetic exact-match handlers minted by WAKE
  // dispatch (`__lab_post_pick__` and friends). Checked before the
  // heal-spot + explicit registry because synthetic refs are
  // runtime-only and should never collide with runtime.json refs.
  const synth = SYNTHETIC_EXACT_HANDLERS[ref];
  if (synth) {
    void synth(ctx, ref);
    return;
  }
  // Layer 2: heal-spot membership
  if (HEAL_SPOT_SCRIPT_REFS.has(ref)) {
    void healHandler(ctx, ref);
    return;
  }
  // Layer 3: explicit registry
  const h = EXPLICIT_HANDLERS[ref];
  if (h) {
    void h(ctx, ref);
    return;
  }
  // Layer 4: unhandled — dev-mode warning, prod silent. Refs that are
  // dialogue-only (no action on close) legitimately fall through here.
  if (import.meta.env.DEV) {
    console.warn(`scriptRunner: no handler for scriptRef "${ref}"`);
  }
}
